import { Link, NavLink } from "react-router-dom";
import {
  Header,
  HeaderContainer,
  HeaderName,
  HeaderMenuButton,
  HeaderMenuItem,
  SkipToContent,
  SideNav,
  SideNavItems,
  HeaderSideNavItems,
} from "@carbon/react";

import { NotificationView } from "./NotificationView";
import { UserIconView } from "./UserIconView";
import { mainNavLinks } from "../../routes";
import { HeaderMenuDropDown } from "../HeaderMenuDropDown";
import { getCurrencySymbol } from "../../utils";
import notificationCircle from "../../assets/icons/notificationCircle.svg";
import userTransparent from "../../assets/icons/userTransparent.svg";
import help from "../../assets/icons/help.svg";
import { HelpView } from "./HelpView";
import { GeneralSearchBar } from "../GeneralSearchBar";
import { Select } from "../Select";
import { AddNewBusinessForm } from "./AddNewBusiness";
import { MerchantSwitcherDropdown } from "./MerchantSwitcherDropdown";

export const View = ({
  signOut,

  // header color for hosting environment
  headerColorClassNameForHostEnv,

  // balances
  isLoadingBalances,
  balances,
  setActiveBalanceCurrency,
  activeBalanceCurrency,

  // others
  filterBusinessesSearchTerm,
  setFilterBusinessesSearchTerm,
  isCreateNewModalOpen,
  openCreateNewModal,
  navigateToProfile,
  navigateToChangePassword,
  navigateToCreatePin,
  navigateToChangePin,
  closeAddBusinessModal,

  isOpenBusinessesDropDown,
  setOpenBusinessesDropDown,

  // access control
  userHasDeveloperAccess,
}) => {
  return (
    <>
      <HeaderContainer
        render={({ isSideNavExpanded, onClickSideNavExpand }) => (
          <Header
            className={`header-transparency-cover ${headerColorClassNameForHostEnv}`}
            aria-label="switchapp header wrapper"
          >
            <Header
              className={`${headerColorClassNameForHostEnv}`}
              aria-label="switchapp header"
            >
              <SkipToContent />
              <HeaderMenuButton
                aria-label="Open menu"
                onClick={onClickSideNavExpand}
                isActive={isSideNavExpanded}
              />
              <HeaderName prefix="" element={Link} to="/">
                <img
                  src={`${process.env.PUBLIC_URL}/switchapp-logo.svg`}
                  alt="switchapp logo"
                  className="app-logo"
                />
              </HeaderName>

              <div
                className="nav-section-desktop"
                aria-label="Account Switching"
              >
                <MerchantSwitcherDropdown
                  isOpenBusinessesDropDown={isOpenBusinessesDropDown}
                  setOpenBusinessesDropDown={setOpenBusinessesDropDown}
                  openCreateNewModal={openCreateNewModal}
                  search={filterBusinessesSearchTerm}
                  setSearch={setFilterBusinessesSearchTerm}
                />

                <div className="flex-container col">
                  <Select
                    name="balanceCurrencySelect"
                    label=""
                    title="Select Currency"
                    placeholder="Select Currency"
                    className="font-ctm--bold currency-select--header"
                    showSkeleton={isLoadingBalances}
                    hideSkeletonLabel
                    selectItems={balances.map((bal) => ({
                      text: `${bal.currency} (${getCurrencySymbol(
                        bal.currency
                      )})`,
                      value: bal.currency,
                    }))}
                    onChange={(e) => setActiveBalanceCurrency(e.target.value)}
                    value={activeBalanceCurrency}
                  />
                </div>

                <div className="header-search-input">
                  <GeneralSearchBar />
                </div>
              </div>

              <div
                className="nav-section-desktop between user-profile-icons"
                aria-label="Help & Documentation"
              >
                <HeaderMenuDropDown
                  headerContainerClass="header-menu-dropdown--container hide-dropdown--sm"
                  headerDropdownContentClass="middle-header-menu-dropdown--content align-self--end"
                  className="header-menu-trigger--button"
                  renderNonListOnOpen={<HelpView />}
                  renderInButton={
                    <div className="help-container">
                      <img aria-label="Notifications" src={help} />
                      <div>Help</div>
                    </div>
                  }
                  headerMenuDropdownContentItemClass="header-menu-dropdown-content-item"
                  headerMenuDropdownButton={true}
                />

                <HeaderMenuDropDown
                  headerContainerClass="circle-header-menu-dropdown--container"
                  headerDropdownContentClass="circle-header-menu-dropdown--content align-self--end"
                  className="circle-header-menu-trigger--button"
                  renderNonListOnOpen={<NotificationView />}
                  renderInButton={
                    <img
                      aria-label="Notifications"
                      className="header-icon-right"
                      src={notificationCircle}
                    />
                  }
                  headerMenuDropdownContentItemClass="circle-header-menu-dropdown-content-item"
                  headerMenuDropdownButton={false}
                />

                <HeaderMenuDropDown
                  headerContainerClass="circle-header-menu-dropdown--container"
                  headerDropdownContentClass="circle-header-menu-dropdown--content align-self--end"
                  className="circle-header-menu-trigger--button"
                  headerMenuDropdownContentItemClass="circle-header-menu-dropdown-content-item"
                  headerMenuDropdownButton={false}
                  renderNonListOnOpen={
                    <UserIconView
                      navigateToProfile={navigateToProfile}
                      navigateToChangePassword={navigateToChangePassword}
                      navigateToCreatePin={navigateToCreatePin}
                      navigateToChangePin={navigateToChangePin}
                      onSignOut={signOut}
                    />
                  }
                  renderInButton={
                    <img
                      aria-label="Notifications"
                      className="header-icon-right"
                      src={userTransparent}
                    />
                  }
                />
              </div>

              <SideNav
                className="side-nav--mobile-only"
                aria-label="Side navigation"
                expanded={isSideNavExpanded}
                isPersistent={false}
              >
                <SideNavItems>
                  <HeaderSideNavItems>
                    <HeaderMenuItem>
                      <MerchantSwitcherDropdown
                        isOpenBusinessesDropDown={isOpenBusinessesDropDown}
                        setOpenBusinessesDropDown={setOpenBusinessesDropDown}
                        openCreateNewModal={openCreateNewModal}
                        search={filterBusinessesSearchTerm}
                        setSearch={setFilterBusinessesSearchTerm}
                      />
                    </HeaderMenuItem>

                    <HeaderMenuItem>
                      <GeneralSearchBar />
                    </HeaderMenuItem>

                    {mainNavLinks.map((link) => (
                      <HeaderMenuItem
                        key={link.route}
                        element={NavLink}
                        to={link.route}
                      >
                        {link.name}
                      </HeaderMenuItem>
                    ))}

                    <hr />
                    {!userHasDeveloperAccess ? null : (
                      <HeaderMenuItem element={Link} to={"/developers"}>
                        Developers
                      </HeaderMenuItem>
                    )}

                    <HeaderMenuItem
                      element={Link}
                      to={"/settings"}
                      className="pad-bottom"
                    >
                      Settings
                    </HeaderMenuItem>
                  </HeaderSideNavItems>
                </SideNavItems>
              </SideNav>
            </Header>
          </Header>
        )}
      />

      <AddNewBusinessForm
        isModalOpen={isCreateNewModalOpen}
        closeModal={closeAddBusinessModal}
      />
    </>
  );
};
